import { ConfirmModal } from '@/components/Bicester';
import BicesterToolbar, { NavTab } from '@/components/layouts/BicesterToolbar';
import { PAGE_TYPE, ROLE_TYPE } from '@/constants';
import usePageType from '@/hooks/usePageType';
import usePrefix from '@/hooks/usePrefix';
import useRouter from '@/hooks/useRouter';
import useTable from '@/hooks/useTable';
import useTabs from '@/hooks/useTabs';
import { useReadyInfo } from '@/providers/ReadyProvider';
import { fetchCustomerList } from '@/services/account';
import { push, replace } from '@/utils/history';
import { useModal } from '@ebay/nice-modal-react';
import { useRequest } from 'ahooks';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

export enum ACCOUNT_TABS {
  MEMBER = 'member',
  NO_MEMBER = 'no-member',
}

export const accountTabs: NavTab[] = [
  { label: '创建会员', key: ACCOUNT_TABS.MEMBER, url: '/customer-account/member/create' },
  {
    label: '创建非会员',
    key: ACCOUNT_TABS.NO_MEMBER,
    url: '/customer-account/no-member/create',
  },
];

interface CAToolbarContextValue {
  listLoading: boolean;
  listData: any;
  onChange: (params: any) => void;
}

export const CAToolbarContext = React.createContext<CAToolbarContextValue>(null);

const CustomerAccount = ({ children }: { children: React.ReactNode }) => {
  const { prefix, classname } = usePrefix('bicester-account');
  const pageType = usePageType();
  const { pathname } = useRouter();
  const { profile } = useReadyInfo();
  const modal = useModal(ConfirmModal);

  const [listData, setListData] = useState({});
  const [content, setContent] = useState('');
  const [isPhoneWord, setIsPhoneWord] = useState(false);

  const {
    run: getCustomerList,
    data: { member_list: customerListData, current_page, total } = {},
    loading: customerListLoading,
  } = useRequest(fetchCustomerList, {
    manual: true,
    onSuccess: (res) => {
      const isPhoneVerify = res?.is_phone_verify === 1;
      setIsPhoneWord(isPhoneVerify);
      if (isPhoneVerify && !res?.member_list?.length) {
        modal.show({
          contentText: '匹配失败，是否创建非会员账户？',
          submitText: '是',
          cancelText: '取消',
          onSubmit() {
            push(
              `/customer-account/${ACCOUNT_TABS.NO_MEMBER}/create${
                isPhoneVerify ? `?phone=${content}` : ''
              }`
            );
          },
        });
      }
    },
  });
  const { params, onChange, onSearch } = useTable(getCustomerList, { content });

  const { tabs, activeTab, onChangeTab } = useTabs<string>(accountTabs);
  const currentTab = useMemo(() => tabs.find((tab) => pathname.includes(tab.url)), [pathname]);

  const getDefaultTab = (navTab) => {
    return _.get(navTab, '[0].key', '');
  };

  useEffect(() => {
    onChangeTab(currentTab ? currentTab.key : getDefaultTab(tabs));
  }, [pathname]);

  useEffect(() => {
    setListData({
      member_list: customerListData,
      current_page,
      total,
    });
  }, [customerListData]);

  return (
    <CAToolbarContext.Provider
      value={{
        listLoading: customerListLoading,
        listData,
        onChange,
      }}
    >
      <div className={classname}>
        <BicesterToolbar
          className={`${
            [ROLE_TYPE.MANAGER, ROLE_TYPE.GANGER, ROLE_TYPE.AMBASSADOR, ROLE_TYPE.DRT].includes(
              profile?.role
            )
              ? ''
              : 'justify-end'
          }`} // GE经理、GE领班、GE迎宾大使、DRT导购大使有创建会员和非会员
        >
          {[ROLE_TYPE.MANAGER, ROLE_TYPE.GANGER, ROLE_TYPE.AMBASSADOR, ROLE_TYPE.DRT].includes(
            profile?.role
          ) && ( // GE经理、GE领班、GE迎宾大使、DRT导购大使有创建会员和非会员
            <BicesterToolbar.NavBar
              data={tabs}
              activeTab={activeTab}
              isActiveDefault={pageType === PAGE_TYPE.CREATE}
              onSelect={(key, event) => setListData({})}
              isPhoneWord={isPhoneWord}
            />
          )}
          <BicesterToolbar.SearchBar
            onChange={setContent}
            onSearch={(value) => {
              onSearch({ content: value });
              replace('/customer-account/list');
            }}
          />
        </BicesterToolbar>
        {children}
      </div>
    </CAToolbarContext.Provider>
  );
};

export default CustomerAccount;
